import type { FC, ReactNode } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import { alpha, lighten, useTheme } from '@mui/material/styles'
import { DrawnCheck } from '@/components/DrawnCheck'
import useTranslate from '@/hooks/use-translate'
import { alertColors } from '@/theme/colors'
import { densityPx } from '@/theme/base/density'
import type { AlertToastSeverity } from '@/utils/alertToastStyles'

const ICON_BOX = densityPx(28)

/** Marque le contenu de la carte, que la pile masque sur les cartes de derrière. */
export const TOAST_CONTENT_ATTRIBUTE = 'data-toast-content'
const content = { [TOAST_CONTENT_ATTRIBUTE]: '' }

/** Point d'exclamation (erreur, avertissement) ou « i » (info), dont le trait se dessine à l'apparition. */
const DrawnMark: FC<{ variant: 'alert' | 'info'; animate: boolean }> = ({
  variant,
  animate,
}) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden>
    <motion.path
      d={variant === 'alert' ? 'M12 4.5v9' : 'M12 10.5v9'}
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      initial={{ pathLength: animate ? 0 : 1 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: animate ? 0.32 : 0, ease: 'easeOut' as const }}
    />
    <circle
      cx="12"
      cy={variant === 'alert' ? 19 : 5.5}
      r="1.8"
      fill="currentColor"
    />
  </svg>
)

const CloseCross = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden>
    <path
      d="M18 6 6 18M6 6l12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
    />
  </svg>
)

const SeverityIcon: FC<{ severity: AlertToastSeverity; animate: boolean }> = ({
  severity,
  animate,
}) => {
  if (severity === 'loading') {
    return <CircularProgress size="1em" thickness={5} color="inherit" />
  }
  if (severity === 'success') return <DrawnCheck animate={animate} />
  return (
    <DrawnMark
      variant={severity === 'info' ? 'info' : 'alert'}
      animate={animate}
    />
  )
}

export interface ToastCardProps {
  severity: AlertToastSeverity
  message: ReactNode
  action?: { label: string; onClick: () => void }
  onClose: () => void
}

/**
 * Carte d'un toast : icône teintée selon la sévérité, message dans la même teinte, croix de fermeture.
 * Le fond reste celui du papier, la couleur ne tient qu'à l'icône et au message.
 */
export const ToastCard: FC<ToastCardProps> = ({
  severity,
  message,
  action,
  onClose,
}) => {
  const theme = useTheme()
  const { t } = useTranslate('common')
  const reduceMotion = useReducedMotion() ?? false
  const { icon, content: text } =
    alertColors[severity === 'loading' ? 'info' : severity]
  // Les teintes de texte d'alertColors sont faites pour un fond clair : en sombre, l'icône éclaircie.
  const messageColor = theme.palette.mode === 'dark' ? lighten(icon, 0.3) : text

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        alignItems: 'start',
        gap: 1.5,
        py: 1.5,
        pl: 1.75,
        pr: 1.25,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: '10px',
        boxShadow: theme.shadows[16],
      }}
    >
      <Box
        {...content}
        aria-hidden
        sx={{
          width: ICON_BOX,
          height: ICON_BOX,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: alpha(icon, 0.14),
          color: icon,
          fontSize: densityPx(16),
        }}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={severity}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.16 } }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            style={{ display: 'inline-flex' }}
          >
            <SeverityIcon severity={severity} animate={!reduceMotion} />
          </motion.span>
        </AnimatePresence>
      </Box>
      <Box
        {...content}
        sx={{
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.5,
          // centre la première ligne sur la pastille de l'icône
          pt: `${(ICON_BOX - densityPx(20)) / 2}px`,
        }}
      >
        <Box
          sx={{
            ...theme.typography.body2,
            fontWeight: 600,
            lineHeight: `${densityPx(20)}px`,
            color: messageColor,
            overflowWrap: 'anywhere',
          }}
        >
          {message}
        </Box>
        {action && (
          <Button
            size="small"
            variant="text"
            onClick={action.onClick}
            sx={{ ml: -1, minWidth: 0 }}
          >
            {action.label}
          </Button>
        )}
      </Box>
      <IconButton
        {...content}
        size="small"
        aria-label={t('close')}
        onClick={onClose}
        sx={{
          color: 'text.secondary',
          borderRadius: '7px',
          fontSize: densityPx(16),
        }}
      >
        <CloseCross />
      </IconButton>
    </Box>
  )
}

export default ToastCard
